import { hangupCall, makeCall } from "../twilio_voice"

type Mode = "phone" | "computer"
type State = "idle" | "starting" | "ringing" | "live" | "ended" | "failed"

interface CheckPayload {
  id: number
  status: string
  duration: number | null
  recording_url: string | null
  recording_duration: number | null
}

const MODE_KEY = "phoneDashboard.callMode"
const POLL_MS = 1000
const ACTIVE_POLL_LIMIT = 600
const RECORDING_POLL_LIMIT = 60
const ENDED: Record<string, string> = {
  completed: "Ended",
  busy: "Busy",
  no_answer: "No answer",
  failed: "Call failed",
  canceled: "Canceled"
}
const UNANSWERED = ["busy", "no_answer", "failed"]

const PHONE_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 4h4l2 5-3 2a12 12 0 005 5l2-3 5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z"/></svg>'
const HANGUP_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 4h4l2 5-3 2a12 12 0 005 5l2-3 5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z"/><path d="M3 3l18 18"/></svg>'

function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === "computer" ? "computer" : "phone"
  } catch (_e) {
    return "phone"
  }
}

function saveMode(mode: Mode): void {
  try {
    localStorage.setItem(MODE_KEY, mode)
  } catch (_e) {
    // a blocked storage only loses the remembered mode
  }
}

export function formatSeconds(total: number): string {
  const secs = Math.max(0, Math.round(total))
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`
}

function request(method: string, url: string, body?: unknown, keepalive = false): Promise<any> {
  return fetch(url, {
    method,
    keepalive,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
    },
    body: body ? JSON.stringify(body) : undefined
  }).then(response =>
    response.json().catch(() => ({})).then(data => {
      if (!response.ok) throw new Error(data?.error || `HTTP ${response.status}`)
      return data
    })
  )
}

export class Dialer {
  callLogId: string | null = null

  private readonly root: HTMLElement
  private readonly botsProspectId: string
  private mode: Mode = readMode()
  private state: State = "idle"
  private seconds = 0
  private timer: number | null = null
  private poller: number | null = null
  private polls = 0
  private recordingPolls = 0
  private endedAs: string | null = null
  private unansweredSent = false
  private destroyed = false

  constructor(root: HTMLElement, botsProspectId: string) {
    this.root = root
    this.botsProspectId = botsProspectId
    this.root.addEventListener("click", this.onClick)
    window.addEventListener("pagehide", this.onPageHide)
    this.paintMode()
    this.paint()
  }

  isLive(): boolean {
    return this.state === "starting" || this.state === "ringing" || this.state === "live"
  }

  // Resolves once the call has ended, so an outcome is never logged while still connected.
  async hangUp(): Promise<void> {
    if (!this.isLive()) return

    if (this.mode === "computer") {
      hangupCall()
      this.end("completed")
      return
    }

    if (!this.callLogId) {
      this.end("canceled")
      return
    }

    try {
      this.apply(await request("PATCH", `/calls/${this.callLogId}/hang_up`))
    } catch (_e) {
      // the poll settles the state if the request failed
    }
    if (this.isLive()) this.end("completed")
  }

  callAgain(): void {
    if (!this.isLive() && !this.destroyed) this.start()
  }

  destroy(): void {
    this.leave()
    this.destroyed = true
    this.stopTimer()
    this.stopPolling()
    this.root.removeEventListener("click", this.onClick)
    window.removeEventListener("pagehide", this.onPageHide)
  }

  private leave(): void {
    if (!this.isLive()) return

    if (this.mode === "computer") hangupCall()
    if (this.callLogId) request("PATCH", `/calls/${this.callLogId}/hang_up`, undefined, true).catch(() => undefined)
  }

  private readonly onPageHide = (): void => this.leave()

  private readonly onClick = (event: Event): void => {
    const target = event.target as HTMLElement
    const modeBtn = target.closest<HTMLElement>("[data-dialer-mode]")
    if (modeBtn && !this.isLive()) {
      this.mode = modeBtn.dataset.dialerMode === "computer" ? "computer" : "phone"
      saveMode(this.mode)
      this.paintMode()
      return
    }

    if (target.closest("[data-dialer-call]")) {
      if (this.isLive()) this.hangUp()
      else this.start()
    }
  }

  private async start(): Promise<void> {
    this.reset()
    this.setState("starting", this.mode === "phone" ? "Calling your phone…" : "Connecting…")

    let data: any
    try {
      data = await request("POST", "/calls", { bots_prospect_id: this.botsProspectId, call_mode: this.mode, record: true })
    } catch (error) {
      this.fail((error as Error).message || "The call couldn't be started.")
      return
    }
    this.callLogId = String(data.call_log_id)
    if (this.destroyed || this.state !== "starting") {
      if (this.mode === "phone") request("PATCH", `/calls/${this.callLogId}/hang_up`).catch(() => undefined)
      return
    }

    if (this.mode === "computer") this.startBrowserCall(data)
    else this.startPolling()
  }

  private async startBrowserCall(data: any): Promise<void> {
    try {
      const call = await makeCall({ To: data.prospect_phone, CallerId: data.caller_id, Record: data.record ? "true" : "false" })
      if (!call) throw new Error("The browser call couldn't connect.")
      if (this.destroyed) {
        hangupCall()
        return
      }

      call.on("ringing", () => this.setState("ringing", "Ringing…"))
      call.on("accept", () => {
        const sid = call.parameters?.CallSid
        if (sid) request("PATCH", `/calls/${this.callLogId}/update_sid`, { call_sid: sid }).catch(() => undefined)
        this.goLive()
      })
      call.on("disconnect", () => this.end("completed"))
      call.on("cancel", () => this.end("canceled"))
      call.on("error", (error: any) => this.fail(error?.message || "The browser call failed."))
    } catch (error) {
      this.fail((error as Error).message || "Microphone or browser calling isn't available.")
    }
  }

  private startPolling(): void {
    this.stopPolling()
    this.poller = window.setInterval(() => this.poll(), POLL_MS)
  }

  private poll(): void {
    if (!this.callLogId || this.destroyed) return

    this.polls += 1
    if (this.isLive() && this.polls > ACTIVE_POLL_LIMIT) {
      this.stopPolling()
      return
    }

    request("GET", `/calls/${this.callLogId}/check`)
      .then(data => this.apply(data))
      .catch(() => undefined)
  }

  private apply(data: CheckPayload): void {
    if (this.destroyed) return

    if (data.status === "initiated" || data.status === "ringing") {
      if (this.state !== "live" && this.mode === "phone") this.setState("ringing", "Ringing…")
    } else if (data.status === "in_progress") {
      if (this.state !== "live") this.goLive()
    } else if (ENDED[data.status]) {
      if (this.isLive()) this.end(data.status, data.recording_duration || data.duration || undefined)
      else if (this.state === "ended" && this.endedAs !== data.status) this.end(data.status)
      this.showRecording(data)
      if (UNANSWERED.includes(data.status)) this.notifyUnanswered(data.status)
    }
  }

  private goLive(): void {
    this.seconds = 0
    this.setState("live", "")
    this.stopTimer()
    this.timer = window.setInterval(() => {
      this.seconds += 1
      this.paintState(`<span class="pdot"></span>In call <span class="dial-timer">${formatSeconds(this.seconds)}</span>`)
    }, 1000)
    this.paintState('<span class="pdot"></span>In call <span class="dial-timer">0:00</span>')
  }

  private end(status: string, duration?: number): void {
    if (this.destroyed) return

    this.stopTimer()
    const secs = duration || this.seconds
    const label = ENDED[status] || "Ended"
    this.endedAs = status
    this.setState("ended", status === "completed" ? `${label} · ${formatSeconds(secs)}` : label)

    // keep polling a little while for the recording
    if (this.callLogId && this.poller === null) this.startPolling()
  }

  private notifyUnanswered(status: string): void {
    if (this.unansweredSent) return

    this.unansweredSent = true
    this.root.dispatchEvent(new CustomEvent("dialer:unanswered", { bubbles: true, detail: { status } }))
  }

  private showRecording(data: CheckPayload): void {
    this.recordingPolls += 1
    if (data.recording_url) {
      const rec = this.root.querySelector<HTMLElement>("[data-dialer-rec]")
      if (rec) {
        rec.innerHTML = `<a href="${data.recording_url}" target="_blank" rel="noopener">Call recording · ${formatSeconds(data.recording_duration || 0)}</a>`
        rec.hidden = false
      }
      this.stopPolling()
    } else if (this.recordingPolls >= RECORDING_POLL_LIMIT || data.status !== "completed") {
      this.stopPolling()
    }
  }

  private fail(message: string): void {
    this.stopTimer()
    this.stopPolling()
    this.setState("failed", message)
  }

  private reset(): void {
    this.stopTimer()
    this.stopPolling()
    this.callLogId = null
    this.seconds = 0
    this.polls = 0
    this.recordingPolls = 0
    this.endedAs = null
    this.unansweredSent = false
    const rec = this.root.querySelector<HTMLElement>("[data-dialer-rec]")
    if (rec) rec.hidden = true
  }

  private setState(state: State, text: string): void {
    this.state = state
    this.root.classList.toggle("live", state === "live")
    this.root.classList.toggle("failed", state === "failed")
    this.paintState(state === "ringing" || state === "starting" ? `<span class="pdot"></span>${text}` : text)
    this.paint()
  }

  private paintState(html: string): void {
    const el = this.root.querySelector<HTMLElement>("[data-dialer-state]")
    if (!el) return
    if (this.state === "failed" || this.state === "ended") el.textContent = html
    else el.innerHTML = html
  }

  private paint(): void {
    const btn = this.root.querySelector<HTMLButtonElement>("[data-dialer-call]")
    if (!btn) return

    const live = this.isLive()
    btn.classList.toggle("btn-green", !live)
    btn.classList.toggle("btn-hangup", live)
    const label = live ? "Hang up" : this.state === "ended" ? "Call again" : this.state === "failed" ? "Try again" : "Call"
    btn.innerHTML = `${live ? HANGUP_SVG : PHONE_SVG}<span data-dialer-label>${label}</span>`
    this.root.querySelectorAll<HTMLButtonElement>("[data-dialer-mode]").forEach(el => { el.disabled = live })
  }

  private paintMode(): void {
    this.root.querySelectorAll<HTMLElement>("[data-dialer-mode]").forEach(el => {
      const on = el.dataset.dialerMode === this.mode
      el.classList.toggle("active", on)
      el.setAttribute("aria-checked", String(on))
    })
  }

  private stopTimer(): void {
    if (this.timer !== null) window.clearInterval(this.timer)
    this.timer = null
  }

  private stopPolling(): void {
    if (this.poller !== null) window.clearInterval(this.poller)
    this.poller = null
  }
}
